import { useEffect, useMemo, useState } from "react";
import { Pencil, Trash2, Package, Search, ExternalLink, AlertCircle, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Modal from "@/components/admin/Modal";
import { PageHeader, Field } from "./AdminFaq";

/* Component categories mirror carts.py PART_CATEGORIES */
const CATEGORIES = [
    { value: "", label: "— todas —" },
    { value: "estrutura", label: "Estrutura" },
    { value: "cobertura", label: "Cobertura" },
    { value: "equipamento", label: "Equipamento" },
    { value: "ferragens", label: "Ferragens" },
    { value: "energia", label: "Energia" },
    { value: "iluminacao", label: "Iluminação" },
    { value: "painel_solar", label: "Painel Solar" },
    { value: "acessorio", label: "Acessório" },
];

const EMPTY = {
    name: "",
    manufacturer: "",
    technical_model: "",
    sku: "",
    category: "",
    default_warranty_months: 24,
    supplier: "",
    documentation_url: "",
    manual_url: "",
    image_url: "",
    description: "",
    notes: "",
};

export default function AdminComponents() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    // Usage / recall panel
    const [usage, setUsage] = useState(null);
    const [usageLoading, setUsageLoading] = useState(false);
    const [usageOpen, setUsageOpen] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const params = {};
            if (q) params.q = q;
            if (cat) params.category = cat;
            const { data } = await api.get("/admin/components", { params });
            setItems(data || []);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao carregar catálogo");
        } finally {
            setLoading(false);
        }
    }
    // Load initially and whenever filter debounces
    useEffect(() => {
        const timer = setTimeout(() => { load(); }, 250);
        return () => clearTimeout(timer);
    }, [q, cat]);

    function openCreate() { setEditing(null); setForm(EMPTY); setModalOpen(true); }
    function openEdit(it) {
        setEditing(it);
        setForm({ ...EMPTY, ...it, default_warranty_months: it.default_warranty_months ?? 24 });
        setModalOpen(true);
    }

    async function save() {
        setSaving(true);
        try {
            const body = { ...form };
            // Clean empty strings for optional fields
            Object.keys(body).forEach((k) => { if (body[k] === "") body[k] = null; });
            if (editing) {
                await api.put(`/admin/components/${editing.id}`, body);
                toast.success("Componente atualizado");
            } else {
                await api.post("/admin/components", body);
                toast.success("Componente criado");
            }
            setModalOpen(false);
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro a guardar");
        } finally { setSaving(false); }
    }

    async function remove(it) {
        if (!window.confirm(`Eliminar "${it.name}"? Esta ação não elimina peças já instaladas em unidades — só remove o item do catálogo.`)) return;
        try {
            await api.delete(`/admin/components/${it.id}`);
            toast.success("Removido");
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro");
        }
    }

    async function openUsage(it) {
        setUsage(null); setUsageOpen(true); setUsageLoading(true);
        try {
            const { data } = await api.get(`/admin/components/${it.id}/usage`);
            setUsage({ ...data, component: it });
        } catch (err) {
            toast.error("Erro a obter utilização");
            setUsageOpen(false);
        } finally { setUsageLoading(false); }
    }

    const catLabel = useMemo(() => Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label])), []);

    return (
        <div className="p-6 space-y-6" data-testid="admin-components-page">
            <PageHeader
                title="Catálogo de Componentes"
                description="Componentes reutilizáveis (fabricante + modelo). Ao instalar num carrinho, apenas se registam os dados específicos daquela unidade (nº série, data, garantia)."
                onAdd={openCreate}
                addLabel="Novo componente"
            />

            {/* Filters */}
            <div className="flex gap-3 items-center bg-white rounded-xl border border-border p-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                        type="search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Pesquisar por nome, fabricante, modelo ou SKU..."
                        className="w-full rounded-md border border-border bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-primary"
                        data-testid="components-search"
                    />
                </div>
                <select
                    value={cat}
                    onChange={(e) => setCat(e.target.value)}
                    className="rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:border-primary"
                    data-testid="components-category-filter"
                >
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-border overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-primary/80 uppercase tracking-wider text-[11px]">
                        <tr>
                            <th className="px-4 py-3 text-left">Componente</th>
                            <th className="px-4 py-3 text-left">Fabricante</th>
                            <th className="px-4 py-3 text-left">Modelo técnico</th>
                            <th className="px-4 py-3 text-left">SKU</th>
                            <th className="px-4 py-3 text-left">Categoria</th>
                            <th className="px-4 py-3 text-center">Garantia</th>
                            <th className="px-4 py-3 text-right">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {loading && (
                            <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">A carregar...</td></tr>
                        )}
                        {!loading && items.length === 0 && (
                            <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">
                                Nenhum componente ainda. Comece por criar (ex: <em>Victron MultiPlus 1600</em>).
                            </td></tr>
                        )}
                        {items.map((it) => (
                            <tr key={it.id} className="hover:bg-muted/30" data-testid={`component-row-${it.id}`}>
                                <td className="px-4 py-3 font-heading font-bold text-primary">
                                    <div className="flex items-center gap-2">
                                        {it.image_url ? (
                                            <img src={it.image_url} alt="" className="h-9 w-9 rounded object-cover" />
                                        ) : (
                                            <div className="h-9 w-9 rounded bg-primary/10 flex items-center justify-center">
                                                <Package className="h-4 w-4 text-primary/60" />
                                            </div>
                                        )}
                                        {it.name}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-primary/80">{it.manufacturer || "—"}</td>
                                <td className="px-4 py-3 text-primary/80 font-mono text-xs">{it.technical_model || "—"}</td>
                                <td className="px-4 py-3 text-primary/60 font-mono text-xs">{it.sku || "—"}</td>
                                <td className="px-4 py-3">
                                    {it.category ? (
                                        <span className="inline-flex rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary">
                                            {catLabel[it.category] || it.category}
                                        </span>
                                    ) : "—"}
                                </td>
                                <td className="px-4 py-3 text-center text-primary/80">
                                    {it.default_warranty_months ? `${it.default_warranty_months} meses` : "—"}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <div className="inline-flex gap-1">
                                        <button
                                            className="p-2 rounded-md hover:bg-muted text-primary/70 hover:text-secondary"
                                            title="Ver unidades onde está instalado (útil para recall)"
                                            onClick={() => openUsage(it)}
                                            data-testid={`component-usage-${it.id}`}
                                        >
                                            <AlertCircle className="h-4 w-4" />
                                        </button>
                                        <button className="p-2 rounded-md hover:bg-muted text-primary/70 hover:text-primary" title="Editar" onClick={() => openEdit(it)}>
                                            <Pencil className="h-4 w-4" />
                                        </button>
                                        <button className="p-2 rounded-md hover:bg-red-50 text-red-500" title="Eliminar" onClick={() => remove(it)}>
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Create / edit modal */}
            <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Editar componente" : "Novo componente"}>
                <div className="space-y-4">
                    <Field label="Nome *">
                        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" placeholder="Ex: Victron MultiPlus 1600" />
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Fabricante / marca">
                            <input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" placeholder="Victron" />
                        </Field>
                        <Field label="Modelo técnico">
                            <input value={form.technical_model} onChange={(e) => setForm({ ...form, technical_model: e.target.value })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm font-mono outline-none focus:border-primary" placeholder="MP-1600" />
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="SKU / referência">
                            <input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm font-mono outline-none focus:border-primary" />
                        </Field>
                        <Field label="Categoria">
                            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary">
                                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Garantia predefinida (meses)">
                            <input type="number" min="0" max="240" value={form.default_warranty_months ?? 24} onChange={(e) => setForm({ ...form, default_warranty_months: Number(e.target.value) })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" />
                        </Field>
                        <Field label="Fornecedor habitual">
                            <input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" />
                        </Field>
                    </div>
                    <Field label="URL da imagem">
                        <input type="url" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" placeholder="/uploads/... ou https://..." />
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="URL do manual (pdf)">
                            <input type="url" value={form.manual_url} onChange={(e) => setForm({ ...form, manual_url: e.target.value })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" />
                        </Field>
                        <Field label="URL documentação técnica">
                            <input type="url" value={form.documentation_url} onChange={(e) => setForm({ ...form, documentation_url: e.target.value })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" />
                        </Field>
                    </div>
                    <Field label="Descrição">
                        <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" />
                    </Field>
                    <Field label="Notas internas">
                        <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" />
                    </Field>
                    <div className="flex justify-end gap-2 pt-2">
                        <button className="px-4 py-2 rounded-md border border-border text-sm" onClick={() => setModalOpen(false)}>Cancelar</button>
                        <button className="px-4 py-2 rounded-md bg-primary text-white text-sm font-bold disabled:opacity-50" onClick={save} disabled={saving || !form.name}>{saving ? "A guardar..." : "Guardar"}</button>
                    </div>
                </div>
            </Modal>

            {/* Usage / recall modal */}
            <Modal open={usageOpen} onClose={() => setUsageOpen(false)} title={usage?.component?.name ? `Utilização: ${usage.component.name}` : "Utilização"}>
                {usageLoading && <div className="p-4 text-center text-muted-foreground">A carregar...</div>}
                {!usageLoading && usage && (
                    <div className="space-y-6">
                        <div>
                            <h4 className="font-heading text-sm font-extrabold text-primary uppercase tracking-wider mb-2 flex items-center gap-2">
                                <Wrench className="h-4 w-4" /> Atualmente instalado em {usage.currently_installed?.length || 0} unidade(s)
                            </h4>
                            {(!usage.currently_installed || usage.currently_installed.length === 0) ? (
                                <p className="text-xs text-muted-foreground italic">Nenhum carrinho tem este componente instalado atualmente.</p>
                            ) : (
                                <ul className="divide-y divide-border rounded-md border border-border">
                                    {usage.currently_installed.map((c) => (
                                        <li key={c.id} className="p-3 flex items-center justify-between gap-3">
                                            <div>
                                                <div className="font-mono text-sm font-bold text-primary">{c.serial_number}</div>
                                                <div className="text-xs text-muted-foreground">{c.commercial_model || c.model}</div>
                                                {c.owner && (
                                                    <div className="text-[11px] text-primary/70 mt-0.5">Cliente: {c.owner.name || c.owner.email}</div>
                                                )}
                                            </div>
                                            <a
                                                href={`/admin/equipments?serial=${encodeURIComponent(c.serial_number)}`}
                                                className="inline-flex items-center gap-1 text-xs text-secondary font-bold"
                                            >
                                                Abrir <ExternalLink className="h-3 w-3" />
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div>
                            <h4 className="font-heading text-sm font-extrabold text-primary uppercase tracking-wider mb-2">
                                Histórico (últimos {usage.history?.length || 0} eventos)
                            </h4>
                            {(!usage.history || usage.history.length === 0) ? (
                                <p className="text-xs text-muted-foreground italic">Sem histórico registado.</p>
                            ) : (
                                <ul className="space-y-2 max-h-64 overflow-y-auto">
                                    {usage.history.slice(0, 30).map((h) => (
                                        <li key={h.id} className="rounded-md border border-border p-2 text-xs">
                                            <div className="flex justify-between">
                                                <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${h.event === "installed" ? "bg-green-100 text-green-700" : h.event === "removed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{h.event}</span>
                                                <span className="text-muted-foreground">{h.at?.slice(0, 10)}</span>
                                            </div>
                                            <div className="mt-1 text-primary/80">
                                                Unidade <span className="font-mono">{h.equipment_id?.slice(0, 8)}...</span> · {h.reason || "sem motivo"}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
